import { Model } from '../../infra/model'
import { makeObservable, observable, computed } from 'mobx'
import { DateZero } from '../../utils/date-util'
import {
  WorkspacePostingStatusDTO,
  WorkspacePostingJobTypeDTO,
} from '../../admin/staffing/dtos/workspace-posting'
import { WorkspacePostingOpportunityDTO } from './dtos/workspace-posting-opportunity'

export enum WorkspacePostingStatus {
  Draft = 'DRAFT',
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  Cancelled = 'CANCELLED',
  InReview = 'IN_REVIEW',
  Unknown = 'UNKNOWN',
}

export default class WorkspacePostingOpportunity extends Model<
  WorkspacePostingOpportunityDTO,
  WorkspacePostingOpportunity
> {
  id: string = ''
  workspaceId: string = ''
  dateCreated: Date = DateZero()
  dateModified: Date = DateZero()
  location: string = ''
  primarySoftware: string = ''
  supplementalSoftware: string = ''
  backupFileNeeded: boolean = false
  specialties: string[] = []
  jobTypes: WorkspacePostingJobType[] = []
  capacity: number = 0
  description: string = ''
  maxAllowedWorkspaces: number = 0
  status: WorkspacePostingStatus = WorkspacePostingStatus.Unknown
  isApplicant = false

  constructor() {
    super()
    makeObservable(this, {
      id: observable,
      workspaceId: observable,
      dateCreated: observable,
      dateModified: observable,
      location: observable,
      primarySoftware: observable,
      supplementalSoftware: observable,
      backupFileNeeded: observable,
      jobTypes: observable,
      capacity: observable,
      description: observable,
      maxAllowedWorkspaces: observable,
      status: observable,
      isApplicant: observable,
      requirements: computed,
      isActive: computed,
      isInReview: computed,
    })
  }

  get requirements() {
    const req = []
    if (this.primarySoftware) {
      req.push(
        this.primarySoftware +
          `${this.backupFileNeeded ? ' with backup file needed' : ''}`,
      )
    }
    if (this.supplementalSoftware) {
      req.push(this.supplementalSoftware)
    }
    return req
  }

  get isActive() {
    return this.status === WorkspacePostingStatus.Active
  }

  get isInReview() {
    return this.status === WorkspacePostingStatus.InReview
  }

  parse(attrs: WorkspacePostingOpportunityDTO) {
    return {
      id: attrs.id,
      workspaceId: attrs.workspace_id,
      dateCreated: attrs.date_created,
      dateModified: attrs.date_modified,
      location: attrs.location,
      primarySoftware: attrs.primary_software,
      supplementalSoftware: attrs.supplemental_software,
      backupFileNeeded: attrs.backup_file_needed,
      jobTypes: attrs.job_types.map(parseWorkspacePostingJobTypeDto),
      specialties: attrs.specialties,
      capacity: attrs.capacity,
      description: attrs.description,
      maxAllowedWorkspaces: attrs.max_allowed_workspaces,
      status: parseWorkspacePostingStatusDto(attrs.status),
      isApplicant: attrs.is_applicant,
    }
  }
}

function parseWorkspacePostingStatusDto(
  dto: WorkspacePostingStatusDTO,
): WorkspacePostingStatus {
  switch (dto) {
    case WorkspacePostingStatusDTO.Draft:
      return WorkspacePostingStatus.Draft
    case WorkspacePostingStatusDTO.Active:
      return WorkspacePostingStatus.Active
    case WorkspacePostingStatusDTO.Closed:
      return WorkspacePostingStatus.Closed
    case WorkspacePostingStatusDTO.Cancelled:
      return WorkspacePostingStatus.Cancelled
    case WorkspacePostingStatusDTO.InReview:
      return WorkspacePostingStatus.InReview
    default:
      return WorkspacePostingStatus.Unknown
  }
}

function parseWorkspacePostingJobTypeDto(
  dto: WorkspacePostingJobTypeDTO,
): WorkspacePostingJobType {
  switch (dto) {
    case WorkspacePostingJobTypeDTO.Prep:
      return WorkspacePostingJobType.Prep
    case WorkspacePostingJobTypeDTO.Review:
      return WorkspacePostingJobType.Review
    case WorkspacePostingJobTypeDTO.Bookkeeping:
      return WorkspacePostingJobType.Bookkeeping
    default:
      return WorkspacePostingJobType.Unknown
  }
}

export enum WorkspacePostingJobType {
  Prep = 'Prep',
  Review = 'Review',
  Bookkeeping = 'Bookkeeping',
  Unknown = 'Unknown',
}
